"use client";

import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useDispatch } from "react-redux";
import {
  InputIcon,
  Input,
  Label,
  Select,
  SelectAction,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectValue,
  Switch,
} from "keep-react";
import {
  User,
  IdentificationCard,
  Eye,
  EyeSlash,
} from "phosphor-react";
import { useStyleSchema } from "@components/styleSchema/StyleSchemaHandler";
import {
  closeActivateExistingAccountModal,
  openRegisterModal,
} from "@store/features/userActionsSlice";
import { activateExistingAccount } from "@store/api-helpers/activateAllreadyExistAccountApi";
import { useActivateExistingAccountCompleteMutation } from "@store/api-helpers/activateExistingAccountCompleteApi";
import { setCredentials } from "@store/features/authSlice";
import { ROUTES } from "@constants/routes";
import { CountrySelect } from "@/components/ui/CountrySelect";
import { BirthdayInput } from "@/components/ui/BirthdayInput";
import { getCountryByCode, getCountryCodeByGermanName } from "@/lib/constants/countries";
import { Salutation } from "@/models/account-activation-complete.model";
import { log } from "@/lib/utils/logger"


// Der Typ für unser Formular (Stufe 1)
type StepOneFormData = {
  firstName: string;
  lastName: string;
  customerNumber: string;
};

// Erweiterter Typ für Stufe 2
interface FormData extends StepOneFormData {
  // Persönliche Informationen
  salutation: string;
  telefon: string;
  dateOfBirth: Date;

  // Adressinformationen
  street: string;
  postalCode: string;
  city: string;
  country: string;

  // Kontoinformationen
  email: string;
  emailConfirm: string;
  password: string;
  passwordConfirm: string;

  // Einwilligungen
  marketingAccepted: boolean;
  privacyAccepted: boolean;
}

interface ActivateAllreadyExistAccountFormProps {
  onStepChange?: (isStepTwo: boolean) => void;
}

export function ActivateAllreadyExistAccountForm(
  props: ActivateAllreadyExistAccountFormProps
) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentSchema } = useStyleSchema();
  const buttonColor = currentSchema.colors?.primary?.hex;

  const [error, setError] = useState("");
  const [disabled, setDisabled] = useState(true);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Status für die zweistufige Formularverarbeitung
  const [isStepTwo, setIsStepTwo] = useState(false);
  const [customer_id, setCustomerId] = useState<string>("");

  // API-Hook für die zweite Stufe der Aktivierung
  const [activateExistingAccountComplete] =
    useActivateExistingAccountCompleteMutation();

  // Benachrichtigung über Statusänderung nach außen
  const { onStepChange } = props;

  // Eigene setIsStepTwo-Funktion, die auch den Callback aufruft
  const updateStepTwo = (newValue: boolean) => {
    setIsStepTwo(newValue);
    if (onStepChange) {
      onStepChange(newValue);
    }
  };

  // Zusätzliche Formularfelder-Status für Stufe 2
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // Basis-Schema für Schritt 1
  const baseSchema = z.object({
    firstName: z
      .string()
      .min(2, { message: t("register.validation.firstName.minLength") }),
    lastName: z
      .string()
      .min(2, { message: t("register.validation.lastName.minLength") }),
    customerNumber: z
      .string()
      .min(5, { message: t("account.validation.customer-number-min-length") }),
  });

  // Erweitertes Schema für Schritt 2
  const fullSchema = isStepTwo
    ? baseSchema
        .extend({
          // Persönliche Informationen
          salutation: z
            .string()
            .min(1, { message: t("register.validation.salutation.required") }),
          telefon: z.string()
            .optional()
            .refine(
              (val) => !val || /^[+]?[0-9\s-()]{8,}$/.test(val),
              {
                message: t("profile.validation.phone_invalid") || "Bitte geben Sie eine gültige Telefonnummer ein",
              }
            ),
          dateOfBirth: z
            .date({
              required_error: t("account.validation.dateOfBirth-required"),
              invalid_type_error: t("account.validation.dateOfBirth-invalid"),
            })
            .refine(
              (date) => {
                const currentYear = new Date().getFullYear();
                return (
                  date.getFullYear() >= 1900 && date.getFullYear() <= currentYear
                );
              },
              {
                message: t("account.validation.dateOfBirth-range", {
                  min: 1900,
                  max: new Date().getFullYear(),
                }),
              }
            ),

          // Adressinformationen
          street: z
            .string()
            .min(1, { message: t("register.validation.streetAndHouseNumber.required") }),
          postalCode: z
            .string()
            .min(5, { message: t("register.validation.postalCode.minLength") }),
          city: z
            .string()
            .min(1, { message: t("register.validation.city.required") })
            .regex(/^[a-zA-ZäöüÄÖÜß\s\-'.]+$/, { 
              message: t("register.validation.city.invalid") || "Bitte geben Sie einen gültigen Ortsnamen ein (erlaubt: Buchstaben, Bindestriche, Apostrophe und Leerzeichen)"
            })
            .transform((val) => val.trim()),
          country: z
            .string()
            .min(1, { message: t("register.validation.country.required") }),

          // Kontoinformationen
          email: z
            .string()
            .email({ message: t("register.validation.email.invalid") }),
          emailConfirm: z
            .string()
            .email({ message: t("register.validation.email.invalid") }),
          password: z
            .string()
            .min(6, { message: t("register.validation.password.minLength") }),
          passwordConfirm: z.string(),

          // Einwilligungen
          marketingAccepted: z.boolean().optional(),
          privacyAccepted: z.boolean().refine((val) => val === true, {
            message: t("register.validation.privacy.required"),
          }),
        })
        .refine((data) => data.email === data.emailConfirm, {
          message: t("register.validation.email.mismatch"),
          path: ["emailConfirm"],
        })
        .refine((data) => data.password === data.passwordConfirm, {
          message: t("register.validation.password.mismatch"),
          path: ["passwordConfirm"],
        })
    : baseSchema;

  // Verwende das entsprechende Schema je nach Formularschritt
  const schema = fullSchema;

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  // Überwachen der Formularfelder für die Button-Aktivierung
  const formValues = watch();

  // Effekt, um den Button basierend auf Formularwerten zu aktivieren/deaktivieren
  useEffect(() => {
    if (!isStepTwo) {
      // Stufe 1: Prüfe auf Vorname, Nachname und Kundennummer (dateOfBirth ist jetzt in Stufe 2)
      const isValid =
        !!formValues.firstName &&
        !!formValues.lastName &&
        !!formValues.customerNumber &&
        !errors.firstName &&
        !errors.lastName &&
        !errors.customerNumber;

      setDisabled(!isValid);
    } else {
      // Stufe 2: Alle Felder prüfen und ob die Nutzungsbedingungen akzeptiert wurden
      const requiredFields = [
        "salutation",
        "dateOfBirth",
        "email",
        "emailConfirm",
        "street",
        "postalCode",
        "city",
        "country",
        "password",
        "passwordConfirm",
      ];

      const allFieldsFilled = !requiredFields.some((field) => {
        const value = formValues[field as keyof FormData];
        return !value;
      });

      // Der Button ist nur aktiv, wenn alle Felder ausgefüllt und die Nutzungsbedingungen akzeptiert wurden
      setDisabled(
        !allFieldsFilled ||
          !formValues.privacyAccepted ||
          Object.keys(errors).length > 0
      );
    }
  }, [formValues, errors, isStepTwo]);

  // Validierung erfolgt jetzt automatisch über useEffect

  // Handler für Formularfelder
  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("firstName", e.target.value);
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("lastName", e.target.value);
  };

  const handleCustomerNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValue("customerNumber", e.target.value);
  };

  // Formular-Einreichung
  const onSubmit = async (data: FormData) => {
    setError("");
    setLoading(true);

    try {
      // Wenn wir noch in Schritt 1 sind, prüfen wir zuerst die Kundendaten
      if (!isStepTwo) {
        const result = await activateExistingAccount({
          firstname: data.firstName,
          lastname: data.lastName,
          customerNumber: data.customerNumber,
        });

        if (result.exists) {
          // Kunden-ID für späteren API-Call speichern
          if (result.customer_id) {
            setCustomerId(result.customer_id);
          }
          updateStepTwo(true);

          // Vorausgefüllte Werte aus den Kundendaten übernehmen
          if (result.customerData) {
            // Telefon
            if (result.customerData.phone) {
              setValue("telefon", result.customerData.phone);
            }

            // Adressdaten
            if (result.customerData.street) {
              setValue("street", result.customerData.street);
            }
            if (result.customerData.zip) {
              setValue("postalCode", result.customerData.zip);
            }
            if (result.customerData.city) {
              setValue("city", result.customerData.city);
            }
            if (result.customerData.country) {
              // Country kommt als deutscher Name (z.B. "Deutschland"), konvertiere zu Code
              const countryCode = getCountryCodeByGermanName(result.customerData.country) || "DE";
              setValue("country", countryCode);
            } else {
              setValue("country", "DE");
            }

            // Geburtstag - Format: MM/dd/yyyy (z.B. "01/01/2007")
            if (result.customerData.birthday) {
              try {
                // Parse MM/dd/yyyy Format
                const [month, day, year] = result.customerData.birthday.split('/');
                const birthDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                if (!isNaN(birthDate.getTime())) {
                  setValue("dateOfBirth", birthDate);
                }
              } catch (error) {
                log.error("Error parsing birthday:", error)
              }
            }

            // E-Mail
            if (result.customerData.email) {
              setValue("email", result.customerData.email);
              setValue("emailConfirm", result.customerData.email);
            }

            // Anrede - DB liefert Zahlen (1=Herr, 2=Frau), Form erwartet i18n-Keys
            if (result.customerData.salutation) {
              const salutationValue = result.customerData.salutation;
              if (salutationValue === Salutation.Herr) {
                setValue("salutation", t("register.salutations.mr"));
              } else if (salutationValue === Salutation.Frau) {
                setValue("salutation", t("register.salutations.mrs"));
              }
            }
          }
        } else {
          // Keine passenden Kundendaten gefunden
          // Schließe das Aktivierungsmodal und öffne das Registrierungsmodal
          dispatch(closeActivateExistingAccountModal());
          dispatch(openRegisterModal());
        }
      } else {
        // Transformiere Country-Code zu deutschem Namen
        const countryName = getCountryByCode(data.country || "DE")?.nameDE || "Deutschland";
        
        // Schritt 2 - Vollständige Aktivierung durch beide API-Calls
        const activationResult = await activateExistingAccountComplete({
          // Authentifizierungsdaten
          email: data.email,
          emailConfirm: data.emailConfirm,
          password: data.password,
          passwordConfirm: data.passwordConfirm,

          // Kundendaten aus Schritt 1
          firstname: data.firstName,
          lastname: data.lastName,
          customerNumber: data.customerNumber,
          dateOfBirth: data.dateOfBirth,
          customer_id: customer_id, // Die Kunden-ID vom ersten Schritt

          // Zusätzliche Kundendaten aus Schritt 2
          salutation: getSalutationEnumValue(data.salutation) || Salutation.Herr,
          telefon: data.telefon || "",
          street: data.street,
          postalCode: data.postalCode,
          city: data.city,
          country: countryName,

          // Einwilligungen
          termsAccepted: data.privacyAccepted,
          marketingAccepted: data.marketingAccepted,
        }).unwrap();

        // Erfolgreiche Aktivierung
        if (activationResult.success && activationResult.data) {
          // Authentifizierung im Redux-Store setzen
          dispatch(
            setCredentials({
              jwt: activationResult.data.jwt,
              user_id: activationResult.data.userId,
              rememberMe: true,
            })
          );

          setSuccess(true);
          
          // Modal sofort schließen und nicht wieder öffnen
          dispatch(closeActivateExistingAccountModal());
          
          // Kurz warten und zur Account-Seite navigieren
          setTimeout(() => {
            window.location.href = ROUTES.myAccount;
          }, 1000);
        }
      }
    } catch (err) {
      // Modal schließen
      setTimeout(() => {
        dispatch(closeActivateExistingAccountModal());
      }, 2000);

      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t("api-errors.unknown-error"));
      }
    } finally {
      setLoading(false);
    }
  };

  // Einfache Funktion für onBlur-Events
  const handleFieldBlur = () => {
    // Validierung erfolgt automatisch durch React Hook Form
  };

  // Hilfsfunktion zum Umschalten der Passwortanzeige
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Hilfsfunktion zum Umschalten der Passwortbestätigungsanzeige
  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Hilfsfunktion zur Konvertierung der Anrede von i18n-Key zu Enum-Wert
  const getSalutationEnumValue = (salutationText: string): Salutation | undefined => {
    if (salutationText === t("register.salutations.mr")) {
      return Salutation.Herr;
    } else if (salutationText === t("register.salutations.mrs")) {
      return Salutation.Frau;
    }
    return undefined;
  };

  // Hilfsfunktion zum Umschalten der Einwilligungen
  const handlePrivacyToggle = () => {
    const newValue = !privacyAccepted;
    setPrivacyAccepted(newValue);
    setValue("privacyAccepted", newValue, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleMarketingToggle = () => {
    const newValue = !marketingAccepted;
    setMarketingAccepted(newValue);
    setValue("marketingAccepted", newValue);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-4">
        {/* Formularüberschrift ändern je nach Schritt */}
        <fieldset>
          <div className="space-y-2">
            <Label
              htmlFor="firstName"
              className="text-[var(--color-slate-600)] font-inter font-medium leading-5 tracking-tight"
            >
              <>
                {t("account.firstname")}
                <span className="text-red-500">*</span>
              </>
            </Label>
            <div className="relative">
              <Input
                id="firstName"
                type="text"
                {...register("firstName")}
                className={
                  errors.firstName
                    ? "border-[var(--color-red-500)] text-[var(--color-red-500)] ps-11 focus:outline-none focus:ring-0 focus-visible:ring-offset-0 focus-visible:border-red-500 focus-visible:ring-0 focus-visible:border-1"
                    : "ps-11 focus:outline-none focus:ring-0 focus-visible:ring-offset-0 focus-visible:border-[var(--color-slate-600)] focus-visible:ring-0 focus-visible:border-1"
                }
                onBlur={handleFieldBlur}
                onChange={handleFirstNameChange}
              />
              <InputIcon>
                <User
                  size={19}
                  className={
                    errors.firstName
                      ? "text-[var(--color-red-500)]"
                      : "text-[#455468]"
                  }
                />
              </InputIcon>
            </div>
            {errors.firstName && (
              <p className="text-red-500 text-sm">{errors.firstName.message}</p>
            )}
          </div>
        </fieldset>

        <fieldset>
          <div className="space-y-2">
            <Label
              htmlFor="lastName"
              className="text-[var(--color-slate-600)] font-inter font-medium leading-5 tracking-tight"
            >
              <>
                {t("account.lastname")}
                <span className="text-red-500">*</span>
              </>
            </Label>
            <div className="relative">
              <Input
                id="lastName"
                type="text"
                {...register("lastName")}
                className={
                  errors.lastName
                    ? "border-[var(--color-red-500)] text-[var(--color-red-500)] ps-11 focus:outline-none focus:ring-0 focus-visible:ring-offset-0 focus-visible:border-red-500 focus-visible:ring-0 focus-visible:border-1"
                    : "ps-11 focus:outline-none focus:ring-0 focus-visible:ring-offset-0 focus-visible:border-[var(--color-slate-600)] focus-visible:ring-0 focus-visible:border-1"
                }
                onBlur={handleFieldBlur}
                onChange={handleLastNameChange}
              />
              <InputIcon>
                <User
                  size={19}
                  className={
                    errors.lastName
                      ? "text-[var(--color-red-500)]"
                      : "text-[#455468]"
                  }
                />
              </InputIcon>
            </div>
            {errors.lastName && (
              <p className="text-red-500 text-sm">{errors.lastName.message}</p>
            )}
          </div>
        </fieldset>

        <fieldset>
          <div className="space-y-2">
            <Label
              htmlFor="customerNumber"
              className="text-[var(--color-slate-600)] font-inter font-medium leading-5 tracking-tight"
            >
              <>
                {t("account.customer-number")}
                <span className="text-red-500">*</span>
              </>
            </Label>
            <div className="relative">
              <Input
                id="customerNumber"
                type="text"
                {...register("customerNumber")}
                className={
                  errors.customerNumber
                    ? "border-[var(--color-red-500)] text-[var(--color-red-500)] ps-11 focus:outline-none focus:ring-0 focus-visible:ring-offset-0 focus-visible:border-red-500 focus-visible:ring-0 focus-visible:border-1"
                    : "ps-11 focus:outline-none focus:ring-0 focus-visible:ring-offset-0 focus-visible:border-[var(--color-slate-600)] focus-visible:ring-0 focus-visible:border-1"
                }
                onBlur={handleFieldBlur}
                onChange={handleCustomerNumberChange}
              />
              <InputIcon>
                <IdentificationCard
                  size={19}
                  className={
                    errors.customerNumber
                      ? "text-[var(--color-red-500)]"
                      : "text-[#455468]"
                  }
                />
              </InputIcon>
            </div>
            {errors.customerNumber && (
              <p className="text-red-500 text-sm">
                {errors.customerNumber.message}
              </p>
            )}
          </div>
        </fieldset>

      </div>

      {error && <p className="text-red-500">{error}</p>}

      {isStepTwo && (
        <>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="salutation">
                  <>
                    {t("register.labels.salutation")}
                    <span className="text-red-500">*</span>
                  </>
                </Label>
                <Controller
                  name="salutation"
                  control={control}
                  render={({ field }) => (
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        setTimeout(() => field.onBlur(), 100);
                      }}
                      value={field.value as string}
                    >
                      <SelectAction>
                        <SelectValue
                          placeholder={t("register.placeholders.salutation")}
                          className={errors.salutation ? "text-red-500" : ""}
                        />
                      </SelectAction>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value={t("register.salutations.mr")}>
                            {t("register.salutations.mr")}
                          </SelectItem>
                          <SelectItem value={t("register.salutations.mrs")}>
                            {t("register.salutations.mrs")}
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.salutation && (
                  <p className="text-red-500 text-sm">
                    {errors.salutation.message as string}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="telefon">
                  <>
                    {t("register.labels.telefon")}
                  </>
                </Label>
                <Input
                  id="telefon"
                  type="tel"
                  placeholder={t("register.placeholders.telefon")}
                  {...register("telefon")}
                />
                {errors.telefon && (
                  <p className="text-red-500 text-sm">
                    {errors.telefon.message as string}
                  </p>
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <BirthdayInput
                name="dateOfBirth"
                control={control}
                label={t("account.birth-date")}
                required={true}
                error={errors.dateOfBirth?.message as string}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4">
          <div className="space-y-2">
                <Label htmlFor="street">
                  <>
                    {t("register.labels.streetAndHouseNumber")}
                    <span className="text-red-500">*</span>
                  </>
                </Label>
                <Input
                  id="street"
                  type="text"
                  placeholder={t("register.placeholders.streetAndHouseNumber")}
                  {...register("street")}
                />
                {errors.street && (
                  <p className="text-red-500 text-sm">
                    {errors.street.message as string}
                  </p>
                )}
              </div>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              

              <div className="space-y-2">
                <Label htmlFor="postalCode">
                  <>
                    {t("register.labels.postalCode")}
                    <span className="text-red-500">*</span>
                  </>
                </Label>
                <Input
                  id="postalCode"
                  type="text"
                  placeholder={t("register.placeholders.postalCode")}
                  {...register("postalCode")}
                />
                {errors.postalCode && (
                  <p className="text-red-500 text-sm">
                    {errors.postalCode.message as string}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="city">
                  <>
                    {t("register.labels.city")}
                    <span className="text-red-500">*</span>
                  </>
                </Label>
                <Input
                  id="city"
                  type="text"
                  placeholder={t("register.placeholders.city")}
                  {...register("city")}
                />
                {errors.city && (
                  <p className="text-red-500 text-sm">
                    {errors.city.message as string}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <CountrySelect
                name="country"
                control={control}
                label={t("register.labels.country")}
                placeholder={t("register.placeholders.country")}
                required={true}
                defaultValue="DE"
                error={errors.country?.message as string}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">
                  <>
                    {t("register.labels.email")}
                    <span className="text-red-500">*</span>
                  </>
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("register.placeholders.email")}
                  {...register("email")}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm">
                    {errors.email.message as string}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="emailConfirm">
                  <>
                    {t("register.labels.emailConfirm")}
                    <span className="text-red-500">*</span>
                  </>
                </Label>
                <Input
                  id="emailConfirm"
                  type="email"
                  placeholder={t("register.placeholders.emailConfirm")}
                  {...register("emailConfirm")}
                />
                {errors.emailConfirm && (
                  <p className="text-red-500 text-sm">
                    {errors.emailConfirm.message as string}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">
                  <>
                    {t("register.labels.password")}
                    <span className="text-red-500">*</span>
                  </>
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t("register.placeholders.password")}
                    {...register("password")}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    {showPassword ? (
                      <EyeSlash size={20} color="#6B7280" />
                    ) : (
                      <Eye size={20} color="#6B7280" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-sm">
                    {errors.password.message as string}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="passwordConfirm">
                  <>
                    {t("register.labels.passwordConfirm")}
                    <span className="text-red-500">*</span>
                  </>
                </Label>
                <div className="relative">
                  <Input
                    id="passwordConfirm"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={t("register.placeholders.passwordConfirm")}
                    {...register("passwordConfirm")}
                  />
                  <button
                    type="button"
                    onClick={toggleConfirmPasswordVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    {showConfirmPassword ? (
                      <EyeSlash size={20} color="#6B7280" />
                    ) : (
                      <Eye size={20} color="#6B7280" />
                    )}
                  </button>
                </div>
                {errors.passwordConfirm && (
                  <p className="text-red-500 text-sm">
                    {errors.passwordConfirm.message as string}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-3 mt-6">
            <div className="flex flex-col">
              <div className="flex items-center gap-4">
                <Controller
                  name="privacyAccepted"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <Switch
                        checked={privacyAccepted}
                        onCheckedChange={(checked) => {
                          handlePrivacyToggle();
                          field.onChange(checked);
                        }}
                        className="bg-primary"
                      />
                    </div>
                  )}
                />
                <p
                  className="flex-1 text-sm font-medium text-gray-700"
                  dangerouslySetInnerHTML={{
                    __html: t("register.terms.label", {
                      termsLink: `<a class="text-[var(--accent-color)] hover:underline" href="${t(
                        "register.links.terms"
                      )}" target="_blank">`,
                      "/termsLink": "</a>",
                      privacyLink: `<a class="text-[var(--accent-color)] hover:underline" href="${t(
                        "register.links.privacy"
                      )}" target="_blank">`,
                      "/privacyLink": "</a>",
                    }),
                  }}
                />
              </div>
              {errors.privacyAccepted && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.privacyAccepted.message as string}
                </p>
              )}
            </div>

            <div className="flex flex-col">
              <div className="flex items-center gap-4">
                <Controller
                  name="marketingAccepted"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <Switch
                        checked={marketingAccepted}
                        onCheckedChange={(checked) => {
                          handleMarketingToggle();
                          field.onChange(checked);
                        }}
                        className="bg-primary"
                      />
                    </div>
                  )}
                />
                <p
                  className="flex-1 text-sm font-medium text-gray-700"
                  dangerouslySetInnerHTML={{
                    __html: t("register.marketing.label", {
                      privacyLink: `<a class="text-[var(--accent-color)] hover:underline" href="${t(
                        "register.links.privacy"
                      )}" target="_blank">`,
                      "/privacyLink": "</a>",
                    }),
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {error && <p className="text-red-500 text-center mt-4 mb-2">{error}</p>}
      {success && (
        <p className="text-green-500 text-center mt-4 mb-2">
          {t("account.activation-success")}
        </p>
      )}

      <div className="w-full flex justify-center items-center">
        <button
          type="submit"
          className="px-4 py-2 rounded text-center md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: buttonColor,
            color: "white",
          }}
          disabled={disabled || loading || (isStepTwo && !privacyAccepted)}
        >
          {loading
            ? t("common.loading")
            : isStepTwo
            ? t("account.activate-account")
            : t("auth.continue")}
        </button>
      </div>
      <p className="text-sm text-slate-600">{t("app.requiredFields")}</p>
    </form>
  );
}
